import { matchPath } from 'react-router-dom';

/**
 * Canonical in-app route PATHS (leading slash, no origin) — single source of truth.
 *
 * Imported by the router (App.jsx), the global nav (HamburgerMenu) and footer
 * (Footer), the pricing cards' demo links (Packages.jsx), the demo banner's
 * back-link (DemoBanner), and the breadcrumb canonical URLs (WebDevPage +
 * FormaFitt/DeruDent demo pages) — so a path rename can't silently desync a
 * <Route> path, a <Link> target (→ 404), the demo chrome-suppression prefix
 * check, or a breadcrumb URL.
 *
 * NOTE: public/sitemap.xml lists only the INDEXABLE routes (webdevService,
 * impresszum) as static literals (XML cannot import JS) — keep those two in sync
 * by hand when their path changes. The demo routes are intentionally omitted from
 * the sitemap (they are noindex; see useNoIndex on the demo pages) — do NOT add
 * them.
 */
export const ROUTES = {
  webdevService: '/weboldal-keszites',
  webdevDemoLanding: '/weboldal-keszites/pelda/landing',
  webdevDemoBemutatkozo: '/weboldal-keszites/pelda/bemutatkozo',
  impresszum: '/impresszum',
};

/**
 * Shared path prefix for the webdev demo microsites. The trailing slash is
 * load-bearing: it scopes the prefix to `/weboldal-keszites/pelda/*` without
 * matching the service page `/weboldal-keszites` itself.
 *
 * NOTE: this is a *string* prefix — do NOT use it to decide layout/chrome.
 * Use `isWebdevDemoPath()` below, which matches the way the router does.
 */
export const WEBDEV_DEMO_PREFIX = '/weboldal-keszites/pelda/';

/** Every route that renders a standalone demo microsite. */
export const WEBDEV_DEMO_ROUTES = [ROUTES.webdevDemoLanding, ROUTES.webdevDemoBemutatkozo];

/**
 * True only for a path that actually resolves to a demo microsite route.
 *
 * Uses `matchPath` rather than `pathname.startsWith(WEBDEV_DEMO_PREFIX)`, because
 * a string prefix disagrees with the router in two ways that both shipped bugs:
 *
 *  1. CASE. React Router matches case-insensitively by default, while
 *     `startsWith` does not. `/Weboldal-Keszites/Pelda/Landing` therefore rendered
 *     the demo page *with* the global footer and cookie banner — chrome the demo
 *     is specifically meant to suppress.
 *
 *  2. NON-ROUTES. A prefix matches paths that resolve to no route at all.
 *     `/weboldal-keszites/pelda/<typo>` suppressed the chrome and then rendered
 *     nothing, stranding the visitor on a blank page with no nav and no footer.
 *
 * Matching the router's own patterns keeps layout policy and routing in agreement
 * by construction, so neither class can recur.
 */
export function isWebdevDemoPath(pathname) {
  return WEBDEV_DEMO_ROUTES.some((path) => matchPath({ path }, pathname) !== null);
}

/**
 * True for the vb-tipp app section: exactly `/vb-tipp` or any `/vb-tipp/*` subpath.
 * Single-sourced here because both the global footer/cookie chrome (App.jsx) and the
 * language switcher (HamburgerMenu) gate on it.
 *
 * Mirrors the `<Route path="/vb-tipp/*">` pattern via `matchPath` — case-insensitive
 * like the router, and (unlike a bare `startsWith('/vb-tipp')`) it does not match
 * unrelated paths such as `/vb-tippXYZ`. The splat also matches the bare `/vb-tipp`.
 */
export function isVbTippPath(pathname) {
  return matchPath({ path: '/vb-tipp/*' }, pathname) !== null;
}
